/**
 * Type Registry - Bidirectional mapping between Scene types and Flow node types
 * Maps selectedCondition/selectedAction IDs to node types and vice versa
 */

export enum ConditionType {
	Device = 1,
	Time = 2,
	Weather = 3,
	SunriseSunset = 4,
	SceneBased = 5,
	EnergyExchange = 6,
	AlarmBased = 7,
	Math = 8,
	Manual = 9,
}

export enum ActionType {
	Device = 1,
	Notify = 2,
	Group = 3,
	Scene = 4,
	Alarm = 5,
	RunScript = 6,
}

export enum TimeConditionSubType {
	Timer = 'timer',
	Daily = 'd_cond',
	ExecuteOnce = 'ot_cond',
}

export enum NotificationSubType {
	Push = 'push_notification',
	Email = 'mail_notification',
	EventLog = 'event_log',
	AlexaNotif = 'alexa_notif',
	AlexaNotifPremium = 'alexa_notif_premium',
}

export enum SceneActionOption {
	WithIfEvaluation = 'with_if_evaluation',
	WithoutIfEvaluation = 'without_if_evaluation',
	Enable = 'action_enable_scene',
	Disable = 'action_disable_scene',
	Toggle = 'action_toggle_scene',
}

export enum AlarmActionOption {
	Enable = 'action_enable_alarm',
	Disable = 'action_disable_alarm',
	Toggle = 'action_toggle_alarm',
}

export interface ConditionTypeInfo {
	id: ConditionType;
	name: string;
	nodeType: string;
	category: 'basic' | 'premium' | 'advanced';
	requiresPremium?: boolean;
	requiresBusiness?: boolean;
	capabilityId: string;
	flowSupport?: 'full' | 'import-only';
}

export interface ActionTypeInfo {
	id: ActionType;
	name: string;
	nodeType: string;
	category: 'basic' | 'advanced';
	capabilityId: string;
	flowSupport?: 'full' | 'import-only';
}

export const CONDITION_TYPES: Record<ConditionType, ConditionTypeInfo> = {
	[ConditionType.Device]: {
		id: ConditionType.Device,
		name: 'Device Based',
		nodeType: 'condition',
		category: 'basic',
		capabilityId: 'device.condition',
	},
	[ConditionType.Time]: {
		id: ConditionType.Time,
		name: 'Time Based',
		nodeType: 'timeCondition',
		category: 'basic',
		capabilityId: 'time.condition',
	},
	[ConditionType.Weather]: {
		id: ConditionType.Weather,
		name: 'Weather Based',
		nodeType: 'weatherCondition',
		category: 'premium',
		requiresPremium: true,
		capabilityId: 'context.condition',
	},
	[ConditionType.SunriseSunset]: {
		id: ConditionType.SunriseSunset,
		name: 'Sunrise/Sunset',
		nodeType: 'sunriseSunsetCondition',
		category: 'premium',
		requiresPremium: true,
		capabilityId: 'time.condition',
	},
	[ConditionType.SceneBased]: {
		id: ConditionType.SceneBased,
		name: 'Scene Based',
		nodeType: 'sceneCondition',
		category: 'advanced',
		capabilityId: 'context.condition',
	},
	[ConditionType.EnergyExchange]: {
		id: ConditionType.EnergyExchange,
		name: 'Energy Exchange',
		nodeType: 'energyCondition',
		category: 'advanced',
		requiresBusiness: true,
		capabilityId: 'context.condition',
	},
	[ConditionType.AlarmBased]: {
		id: ConditionType.AlarmBased,
		name: 'Alarm Based',
		nodeType: 'alarmCondition',
		category: 'advanced',
		capabilityId: 'context.condition',
	},
	[ConditionType.Math]: {
		id: ConditionType.Math,
		name: 'Math Expression',
		nodeType: 'mathCondition',
		category: 'advanced',
		capabilityId: 'math.condition',
	},
	[ConditionType.Manual]: {
		id: ConditionType.Manual,
		name: 'Manual Execution',
		nodeType: 'manualCondition',
		category: 'basic',
		capabilityId: 'manual.condition',
	},
};

export const ACTION_TYPES: Record<ActionType, ActionTypeInfo> = {
	[ActionType.Device]: {
		id: ActionType.Device,
		name: 'Device Action',
		nodeType: 'action',
		category: 'basic',
		capabilityId: 'device.action',
	},
	[ActionType.Notify]: {
		id: ActionType.Notify,
		name: 'Notification',
		nodeType: 'notificationAction',
		category: 'basic',
		capabilityId: 'notification.action',
	},
	[ActionType.Group]: {
		id: ActionType.Group,
		name: 'Group Action',
		nodeType: 'groupAction',
		category: 'advanced',
		capabilityId: 'automation.action',
	},
	[ActionType.Scene]: {
		id: ActionType.Scene,
		name: 'Scene Action',
		nodeType: 'sceneAction',
		category: 'advanced',
		capabilityId: 'automation.action',
	},
	[ActionType.Alarm]: {
		id: ActionType.Alarm,
		name: 'Alarm Action',
		nodeType: 'alarmAction',
		category: 'advanced',
		capabilityId: 'automation.action',
	},
	[ActionType.RunScript]: {
		id: ActionType.RunScript,
		name: 'Run Script',
		nodeType: 'scriptAction',
		category: 'advanced',
		capabilityId: 'automation.action',
	},
};

/**
 * Get condition type info by ID
 */
export function getConditionTypeInfo(id: ConditionType): ConditionTypeInfo | null {
	return CONDITION_TYPES[id] || null;
}

/**
 * Get action type info by ID
 */
export function getActionTypeInfo(id: ActionType): ActionTypeInfo | null {
	return ACTION_TYPES[id] || null;
}

/**
 * Get condition type by node type string
 */
export function getConditionTypeByNodeType(nodeType: string): ConditionType | null {
	for (const [key, value] of Object.entries(CONDITION_TYPES)) {
		if (value.nodeType === nodeType) {
			return parseInt(key) as ConditionType;
		}
	}
	return null;
}

/**
 * Get action type by node type string
 */
export function getActionTypeByNodeType(nodeType: string): ActionType | null {
	for (const [key, value] of Object.entries(ACTION_TYPES)) {
		if (value.nodeType === nodeType) {
			return parseInt(key) as ActionType;
		}
	}
	return null;
}

/**
 * Get all available condition types (filtered by feature access)
 */
export function getAvailableConditionTypes(
	hasPremium: boolean = false,
	hasBusiness: boolean = false,
): ConditionTypeInfo[] {
	return Object.values(CONDITION_TYPES).filter((type) => {
		if (type.requiresPremium && !hasPremium) return false;
		if (type.requiresBusiness && !hasBusiness) return false;
		return true;
	});
}

/**
 * Get all available action types
 */
export function getAvailableActionTypes(): ActionTypeInfo[] {
	return Object.values(ACTION_TYPES);
}
